import React from "react";
import { ListChecks, DoorOpen } from "lucide-react";
import { Card, Input, Toggle, Textarea, TagGroup, Select, TimePicker } from "./FormComponents";

export const Step5 = ({ data, updateData, buildingAccess, isLight, showValidation }) => {
  const handleChange = (field, value) => {
    updateData("checkOut", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("checkOut", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleBuildingExitChange = (field, value) => {
    updateData("checkOut", {
      ...data,
      buildingExit: {
        ...data.buildingExit,
        [field]: value
      }
    });
  };

  const showBuildingExit = buildingAccess?.hasMainEntrance;

  return (
    <div className="flex flex-col gap-8">
      <Card title="Departure Checklist" icon={ListChecks} subtitle="Departure process and key return." isLight={isLight}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TimePicker 
            label="Check-out time" 
            required 
            value={data.time || "11:00"}
            isLight={isLight}
            showValidation={showValidation}
            onChange={(val) => handleChange("time", val)}
          />
          <div className="md:col-span-2">
            <Toggle 
              label="Late check-out available upon request?" 
              value={data.lateCheckOut.available}
              isLight={isLight}
              onChange={(val) => {
                const updates = { available: val };
                if (!val) {
                  updates.feeApplicable = false;
                  updates.feeAmount = "";
                  updates.feeCurrency = "€";
                }
                updateData("checkOut", {
                  ...data,
                  lateCheckOut: { ...data.lateCheckOut, ...updates }
                });
              }}
            >
              <Toggle 
                label="Is there a late check-out fee?" 
                value={data.lateCheckOut.feeApplicable}
                isLight={isLight}
                onChange={(val) => handleNestedChange("lateCheckOut", "feeApplicable", val)}
              >
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-400">Late check-out fee <span className="text-red-500">*</span></label>
                  <div className="flex gap-3 items-start max-w-xs">
                    <Input 
                      type="text"
                      inputMode="decimal"
                      placeholder="15" required
                      value={data.lateCheckOut.feeAmount}
                      isLight={isLight}
                      showValidation={showValidation}
                      className="w-32"
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, "");
                        const parts = val.split(".");
                        const filteredVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val;
                        handleNestedChange("lateCheckOut", "feeAmount", filteredVal);
                      }}
                    />
                    <Select 
                      options={["€"]} className="w-20" 
                      value={data.lateCheckOut.feeCurrency}
                      isLight={isLight}
                      showValidation={showValidation}
                      hidePlaceholder
                      onChange={(e) => handleNestedChange("lateCheckOut", "feeCurrency", e.target.value)}
                    />
                  </div>
                </div>
              </Toggle>
            </Toggle>
          </div>
          <div className="md:col-span-2">
            <Toggle 
              label="Can guests request a stay extension, depending on availability?" 
              value={data.stayExtension.available}
              isLight={isLight}
              onChange={(val) => handleNestedChange("stayExtension", "available", val)}
            />
          </div>

          <div className="md:col-span-2">
            <Toggle 
              label="Are guests expected to do any cleaning before check-out?" 
              value={data.checkoutCleaning.expected}
              isLight={isLight}
            onChange={(val) => {
                const updates = { expected: val };
                if (!val) {
                  updates.tasks = [];
                  updates.otherTask = "";
                }
                updateData("checkOut", {
                  ...data,
                  checkoutCleaning: { ...data.checkoutCleaning, ...updates }
                });
              }}
            >
              <div className="flex flex-col gap-6">
                <TagGroup 
                  label="Required cleaning tasks" required={data.checkoutCleaning.expected}
                  options={["Basic tidying", "Kitchen cleaning", "Trash removal", "Other"]}
                  value={data.checkoutCleaning.tasks}
                  isLight={isLight}
                  showValidation={showValidation}
                  onChange={(val) => {
                    const updates = { tasks: val };
                    if (!val.includes("Other")) updates.otherTask = "";
                    updateData("checkOut", {
                      ...data,
                      checkoutCleaning: { ...data.checkoutCleaning, ...updates }
                    });
                  }}
                />
                {data.checkoutCleaning.tasks.includes("Other") && (
                  <Input 
                    label="Other cleaning instructions" placeholder="Please strip the beds" required
                    value={data.checkoutCleaning.otherTask}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("checkoutCleaning", "otherTask", e.target.value)}
                  />
                )}
              </div>
            </Toggle>
          </div>

          <div className="md:col-span-2">
            <Textarea 
              label="Key/card return instructions" 
              placeholder="Leave keys in the lockbox. Close and scramble the numbers." required
              value={data.keyReturn}
              isLight={isLight}
              showValidation={showValidation}
              onChange={(e) => handleChange("keyReturn", e.target.value)}
            />
          </div>

          <div className="md:col-span-2">
            <Textarea 
              label="General check-out tasks" 
              placeholder="Turn off all lights and AC. Close windows. Leave dishes washed." 
              value={data.tasks}
              isLight={isLight}
              showValidation={showValidation}
              onChange={(e) => handleChange("tasks", e.target.value)}
            />
          </div>
        </div>
      </Card>

      {showBuildingExit && (
        <Card 
          title="Exiting Through the Main Building Entrance" 
          icon={DoorOpen} 
          subtitle="Use this if guests may need a key, code, fob, lockbox, or special instructions to leave the building during check-out." 
          isLight={isLight}
        >
          <div className="flex flex-col gap-8">
            <div className="pb-4 border-b border-white/5 flex flex-col gap-4">
              <span className={`text-sm font-medium ${isLight ? 'text-neutral-700' : 'text-neutral-400'}`}>Are there times when guests need a key, code, or special instructions to exit through the main building entrance?</span>
              <div className="flex flex-col gap-2">
                {[
                  "No, the building exit opens freely from inside",
                  "Yes, only during certain hours",
                  "Yes, every time they exit."
                ].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      const updates = { accessType: option };
                      if (option === "No, the building exit opens freely from inside") {
                        updates.accessFrom = "";
                        updates.accessUntil = "";
                        updates.exitInstructions = "";
                      } else if (option === "Yes, every time they exit.") {
                        updates.accessFrom = "";
                        updates.accessUntil = "";
                      } else if (option === "Yes, only during certain hours") {
                        if (!data.buildingExit.accessFrom) updates.accessFrom = "22:00";
                        if (!data.buildingExit.accessUntil) updates.accessUntil = "07:00";
                      }
                      updateData("checkOut", {
                        ...data,
                        buildingExit: { ...data.buildingExit, ...updates }
                      });
                    }}
                    className={`text-left px-5 py-4 rounded-3xl border text-sm transition-all ${
                      data.buildingExit.accessType === option
                        ? isLight
                          ? "bg-neutral-900 border-neutral-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.55)]"
                          : "bg-white/20 border-white/10 text-white shadow-[0_12px_30px_rgba(0,0,0,0.8)]"
                        : isLight 
                          ? "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50 shadow-sm" 
                          : "bg-white/5 border-white/5 text-neutral-400 hover:border-white/15 hover:bg-white/10"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              {data.buildingExit.accessType === "Yes, only during certain hours" && (
                <div className="flex items-center gap-2 mt-2">
                  <TimePicker 
                    label="From" 
                    value={data.buildingExit.accessFrom || "22:00"}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={(val) => handleBuildingExitChange("accessFrom", val)}
                    required
                  />
                  <TimePicker 
                    label="Until" 
                    value={data.buildingExit.accessUntil || "07:00"}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={(val) => handleBuildingExitChange("accessUntil", val)}
                    required
                  />
                </div>
              )}

              {(data.buildingExit.accessType === "Yes, only during certain hours" || data.buildingExit.accessType === "Yes, every time they exit.") && (
                <div className="mt-4">
                  <Textarea 
                    label="Main entrance exit instructions" 
                    placeholder="E.g., Guests can use the internal button next to the door to exit." 
                    value={data.buildingExit.exitInstructions}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleBuildingExitChange("exitInstructions", e.target.value)}
                    required
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
